/**
 * The pre-screen's answer rules, shared by the family's screen
 * (`components/apply/EligibilityCheck.tsx`, where they were born) and the
 * builder's "See it as a family" preview (#3112). One copy, so the preview
 * cannot call an answer complete that the family's button refuses.
 *
 * Answers are flat strings keyed by field id — the shape the pre-screen keeps.
 */
import type { DefinitionField } from '@/lib/formDefinitions';

export type PrescreenAnswers = Record<string, string>;

/**
 * The ceiling a numeric question is held to, and the digit cap that must agree
 * with it. `age` ships `min: 0` and NO `max`, so the fallback is doing real
 * work rather than sitting unused — and a hardcoded `maxLength={2}` against a
 * fallback of 120 promised a range the box could not physically accept. The
 * cap is derived from the ceiling so the two cannot drift apart again, whatever
 * a re-ranged definition says.
 */
export function numericMax(field: DefinitionField): number {
  return field.max ?? 120;
}

/**
 * Why a numeric answer is not an answer yet — or `null` when it is.
 *
 * THE BOUNDS COME FROM THE DEFINITION (`field.min` / `field.max`), and they are
 * enforced HERE rather than by the input, because the box is `type="text"`: the
 * narrow numeric field needs `inputMode` + `pattern` + `maxLength` to get a
 * digit keypad and a digit cap on mobile, and per the HTML spec `min`/`max`
 * are inert on anything but `type="number"`. `maxLength` bounds the box by
 * DIGIT COUNT, which is a coarser thing than a range — a 2-digit cap still
 * admits 99 against a ceiling of 25. So the ceiling is this function's job,
 * and `isComplete` calls it too: an out-of-range answer is blocked from
 * submitting, not merely flagged.
 *
 * Returns the range the answer is asked for. The sentence ("Enter a whole
 * number from 0 to 120.") is the validation catalog's `number.wholeBetween`,
 * in the reader's language (#2109 R2).
 */
export function numericAnswerError(field: DefinitionField, raw: string): { min: number; max: number } | null {
  const min = field.min ?? 0;
  const max = numericMax(field);
  const ask = { min, max };
  const value = raw.trim();
  if (value === '') return ask;
  if (!/^\d+$/.test(value)) return ask;
  const numeric = Number(value);
  if (numeric < min || numeric > max) return ask;
  return null;
}

/** Every live question this family is ASKED, answered — the submit gate.
 *  `fields` is the revealed set (#1639), never the whole definition: demanding
 *  a question the family cannot see is a screen with no way forward, and the
 *  server's own `unanswered` list does not demand it either. */
export function isAnswered(field: DefinitionField, answers: PrescreenAnswers): boolean {
  const value = (answers[field.id] ?? '').trim();
  if (value === '') return false;
  // A numeric answer counts only if it is one: a negative, a stray character
  // or a value outside the definition's range is not an answer. The form is
  // `noValidate`, so nothing else stops it.
  if (field.type === 'number') return numericAnswerError(field, value) === null;
  return true;
}

export function isComplete(fields: DefinitionField[], answers: PrescreenAnswers): boolean {
  return fields.every((field) => isAnswered(field, answers));
}

/**
 * The labels named under the disabled check button (2026-10-11, J1 c04): the
 * asked questions still open, but only once the family has answered anything
 * — and none once the form is complete. The sentence around them is the
 * `forms` catalog's `prescreenChrome.stillToAnswer`.
 */
export function stillToAnswer(fields: DefinitionField[], answers: PrescreenAnswers): string[] {
  if (isComplete(fields, answers)) return [];
  if (!fields.some((f) => (answers[f.id] ?? '').trim() !== '')) return [];
  return fields.filter((f) => !isAnswered(f, answers)).map((f) => f.label);
}
